import type { FC, ReactNode } from 'react'
import type { StockStatus } from '@/types'

import { cn } from '@/lib/utils'
import { CircleCheck, CircleX, TriangleAlert } from 'lucide-react'
import { useSettings } from '@/components/generals/settings-provider'
import { STOCK_STATUS_META } from '@/constants/inventory'

interface StockHealthProps {
    ok: number
    low: number
    out: number
}

const FILL: Record<StockStatus, string> = {
    ok: 'bg-success',
    low: 'bg-warning',
    out: 'bg-danger'
}

const INK: Record<StockStatus, string> = {
    ok: 'text-success-ink',
    low: 'text-warning-ink',
    out: 'text-danger-ink'
}

const ICONS = {
    ok: CircleCheck,
    low: TriangleAlert,
    out: CircleX
}

const ORDER: StockStatus[] = ['ok', 'low', 'out']

const StockHealth: FC<StockHealthProps> = ({ ok, low, out }): ReactNode => {
    const { number, percent } = useSettings()
    const values: Record<StockStatus, number> = { ok, low, out }
    const total = ok + low + out

    if (!total) {
        return (
            <p className='text-sm text-muted-foreground'>
                Cuando añadas productos verás aquí cuántos están bien, cuántos bajos y cuántos agotados.
            </p>
        )
    }

    return (
        <div className='flex flex-col gap-4'>
            <div className='flex h-3 w-full gap-0.5 overflow-hidden rounded-full' aria-hidden='true'>
                {ORDER.filter(status => values[status] > 0).map(status => (
                    <div
                        key={status}
                        title={`${STOCK_STATUS_META[status].label}: ${number(values[status])}`}
                        className={cn('h-full transition-[flex-grow] duration-500', FILL[status])}
                        style={{ flexGrow: values[status], flexBasis: 0 }}
                    />
                ))}
            </div>

            <ul className='flex flex-col gap-2.5'>
                {ORDER.map(status => {
                    const Icon = ICONS[status]

                    return (
                        <li key={status} className='flex items-center justify-between gap-3 text-sm'>
                            <span className='flex items-center gap-2'>
                                <Icon className={cn('size-4', INK[status])} aria-hidden='true' />
                                {STOCK_STATUS_META[status].label}
                            </span>

                            <span className='tabular-nums'>
                                <span className='font-semibold'>{number(values[status])}</span>
                                <span className='ml-1.5 text-xs text-muted-foreground'>{percent((values[status] / total) * 100)}</span>
                            </span>
                        </li>
                    )
                })}
            </ul>
        </div>
    )
}

export default StockHealth
